/**
 * ObstructionDrawer Component
 * Interactive polygon drawing tool for obstructions
 *
 * Extracted from GeoTiffViewerEmbedded.tsx (lines 496-615)
 * Similar to PolygonDrawer but with red color scheme for obstructions
 */

import React, { useState, useEffect } from 'react';
import { useMap } from 'react-leaflet';
import { CircleMarker, Polyline } from 'react-leaflet';

export interface ObstructionDrawerProps {
  isDrawing: boolean;
  vertices: [number, number][];
  onAddVertex: (vertex: [number, number]) => void;
  onFinish: () => void;
  onCancel: () => void;
}

/**
 * Interactive obstruction polygon drawing component
 * Red color scheme to distinguish from roof polygons
 */
export function ObstructionDrawer({
  isDrawing,
  vertices = [], // Default to empty array if undefined
  onAddVertex,
  onFinish,
  onCancel,
}: ObstructionDrawerProps) {
  const map = useMap();
  const [cursorPos, setCursorPos] = useState<[number, number] | null>(null);
  const [isHoveringOrigin, setIsHoveringOrigin] = useState(false);

  // Check if a point is close to the origin (first vertex) - for click-to-close
  const isNearOrigin = (clickLatLng: { lat: number; lng: number }): boolean => {
    if (vertices.length < 3) return false;

    const origin = vertices[0];
    // Use map zoom to calculate appropriate threshold
    const zoom = map.getZoom();
    // At zoom 20, ~0.00001 is about 1 meter. Scale threshold based on zoom.
    const threshold = 0.0001 * Math.pow(2, 18 - zoom);

    const distance = Math.sqrt(
      Math.pow(clickLatLng.lat - origin[0], 2) +
      Math.pow(clickLatLng.lng - origin[1], 2)
    );

    return distance < threshold;
  };

  useEffect(() => {
    if (!isDrawing) {
      setCursorPos(null);
      return;
    }

    const handleClick = (e: any) => {
      e.originalEvent.preventDefault();
      e.originalEvent.stopPropagation();

      // Check if clicking near origin to close polygon
      if (isNearOrigin(e.latlng)) {
        onFinish();
        return;
      }

      onAddVertex([e.latlng.lat, e.latlng.lng]);
    };

    const handleDblClick = (e: any) => {
      e.originalEvent.preventDefault();
      e.originalEvent.stopPropagation();
      if (vertices.length >= 3) {
        onFinish();
      }
    };

    const handleMouseMove = (e: any) => {
      setCursorPos([e.latlng.lat, e.latlng.lng]);
      // Check if hovering near origin for visual feedback
      setIsHoveringOrigin(isNearOrigin(e.latlng));
    };

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        onCancel();
      } else if (e.key === 'Enter' && vertices.length >= 3) {
        onFinish();
      }
    };

    map.on('click', handleClick);
    map.on('dblclick', handleDblClick);
    map.on('mousemove', handleMouseMove);
    window.addEventListener('keydown', handleKeyDown);

    return () => {
      map.off('click', handleClick);
      map.off('dblclick', handleDblClick);
      map.off('mousemove', handleMouseMove);
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [isDrawing, map, vertices, onAddVertex, onFinish, onCancel]);

  if (!isDrawing) return null;

  return (
    <>
      {/* Vertex markers - Red for obstructions */}
      {vertices.map((vertex, index) => {
        const isOrigin = index === 0;
        const canClose = isOrigin && vertices.length >= 3;
        const isOriginHighlighted = canClose && isHoveringOrigin;

        return (
          <CircleMarker
            key={`obstruction-vertex-${index}`}
            center={vertex}
            // @ts-ignore - react-leaflet v5 supports radius but v2 types don't
            radius={isOriginHighlighted ? 9 : (canClose ? 7 : 5)}
            pathOptions={{
              color: isOriginHighlighted ? '#22c55e' : (canClose ? '#f97316' : '#dc2626'),
              fillColor: isOriginHighlighted ? '#22c55e' : (canClose ? '#f97316' : '#dc2626'),
              fillOpacity: 1,
              weight: isOriginHighlighted ? 3 : 2,
            }}
          />
        );
      })}

      {/* Polyline connecting vertices */}
      {vertices.length > 1 && (
        <Polyline
          positions={vertices}
          pathOptions={{
            color: '#dc2626',
            weight: 2,
            dashArray: '5, 5',
          }}
        />
      )}

      {/* Preview line from last vertex to cursor */}
      {vertices.length > 0 && cursorPos && (
        <Polyline
          positions={[vertices[vertices.length - 1], cursorPos]}
          pathOptions={{
            color: '#dc2626',
            weight: 2,
            dashArray: '5, 5',
            opacity: 0.6,
          }}
        />
      )}

      {/* Closing line preview when ready to finish */}
      {vertices.length >= 3 && cursorPos && (
        <Polyline
          positions={[cursorPos, vertices[0]]}
          pathOptions={{
            color: isHoveringOrigin ? '#22c55e' : '#ef4444',
            weight: isHoveringOrigin ? 3 : 2,
            dashArray: isHoveringOrigin ? undefined : '5, 5',
            opacity: isHoveringOrigin ? 0.9 : 0.4,
          }}
        />
      )}
    </>
  );
}
